/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineUserGroup, HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import type { Action, Column } from "../../ui";
import { usePartnerContext } from "../../context/PartnerContext";
import { pushOnePartner } from "../../lib/sysNavigation/SysNavigation";
import { _PAGE_SIZE_ } from "../../lib/other/tools";

type Partner = {
  partner_id: number | string;
  name?: string;
  description?: string;
  thumbnail?: string;
  [key: string]: unknown;
};

const addRoute = "/partner/add";
const editRoute = "/partner/edit";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { partner, count, fetchPartners, deletePartner, updatePartnerState } =
    usePartnerContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!partner) fetchPartners(page, pageSize);
  }, [partner]);

  const rows = useMemo<Partner[] | null>(() => {
    if (!Array.isArray(partner)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return partner as Partner[];
    return (partner as Partner[]).filter((p) =>
      (p.name ?? "").toString().toLowerCase().includes(q),
    );
  }, [partner, query]);

  const columns: Column<Partner>[] = [
    {
      header: "Name",
      cell: (row) => (
        <div className="flex items-center gap-3">
          {row.thumbnail ? (
            <img
              src={row.thumbnail}
              alt={row.name ?? ""}
              className="h-10 w-10 rounded-full object-cover ring-1 ring-gray-200"
            />
          ) : (
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
              <HiOutlineUserGroup className="h-4 w-4" />
            </span>
          )}
          <span className="font-medium text-gray-900">{row.name ?? "—"}</span>
        </div>
      ),
    },
    {
      header: "Description",
      cell: (row) => (
        <p className="line-clamp-2 text-gray-600">{row.description ?? ""}</p>
      ),
    },
  ];

  const handleDelete = async (row: Partner) => {
    const response: any = await deletePartner(row.partner_id);
    if (response?.status === 200 && Array.isArray(partner)) {
      updatePartnerState(
        (partner as Partner[]).filter((p) => p.partner_id !== row.partner_id),
      );
    }
  };

  const rowActions = (row: Partner): Action[] => [
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOnePartner(row);
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Delete ${row.name ?? "partner"}?`,
        description: "This partner will no longer appear on the public site.",
        confirmLabel: "Delete",
      },
    },
  ];

  return (
    <ListPage<Partner>
      title="Partners"
      subtitle="Organizations showcased on your public site."
      icon={<HiOutlineUserGroup className="h-5 w-5" />}
      actions={[{ label: "Add partner", variant: "primary", href: addRoute }]}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.partner_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search partners…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchPartners(p, ps);
        },
      }}
      empty="No partners yet."
    />
  );
};

export default Overview;
